import {
  Close,
  WhatsApp,
  Preview,
  Article,
  Group,
  Link as LinkIcon,
  OpenInNew,
  Lightbulb,
  AutoAwesomeMosaic,
} from "@mui/icons-material";
import {
  Stack,
  Button,
  Box,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Dialog,
  DialogContent,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import CSVIcon from "../../../assets/icons/csv-file-icon.png";
import ExcelIcon from "../../../assets/icons/excel-file-icon.png";
import React from "react";
import { IMarketCampaignBasicInfo } from "../../../redux/marketCampaign/marketCampaign.types";
import { RightPanel } from "../../../components/RightPanel";
import {
  clearDraftStepState,
  fetchMarketCampaignAudienceAsync,
  fetchMarketCampaignChannelListAsync,
  fetchMarketCampaignDraftStepsAsync,
} from "../../../redux/marketCampaign/marketCampaignActions";
import { useDispatchWrapper } from "../../../hooks";
import { IStoreState } from "../../../redux/initialStoreState";
import { useSelector } from "react-redux";
import { PreviewTemplate } from "../../../components/Preview/PreviewTemplate";
import { GridItem } from "../../../components/GridItem/GridItem";
import { convertToWhatsAppTemplateData } from "../../../helpers/convertTemplateData";
import WhatsappBgImg from "../../../assets/whatsapp_bg_img.jpg";
import {
  clearWhatsAppTemplateState,
  fetchWhatsAppTemplateAsync,
  fetchWhatsAppTemplateListAsync,
} from "../../../redux/whatsappTemplate/whatsappTemplateAction";
import { IWhatsAppTemplateData } from "../../../redux/whatsappTemplate/whatsappTemplate.types";
import { ViewImageDialog } from "../../../components/FileUploadIcon/ViewImageDialog";
import { useFilePreview } from "../../../hooks/useFilePreview";
import { formatText } from "../../../helpers";

interface IMarketingRightPanelProps {
  open: boolean;
  data: IMarketCampaignBasicInfo;
  onClose: () => void;
}

export const ViewMarketingCampaignRightPanel: React.FC<
  IMarketingRightPanelProps
> = (props) => {
  const { open, data, onClose } = props;
  const dispatch = useDispatchWrapper();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const { preview, previewFile, closePreview } = useFilePreview();

  const { list: metaTemplates } = useSelector(
    (state: IStoreState) => state.whatsappTemplate.list,
  );
  const { data: draftStepData } = useSelector(
    (storeState: IStoreState) =>
      storeState.marketCampaign.marketCampaignDraftSteps,
  );
  const { data: marketCampaignAudienceData } = useSelector(
    (storeState: IStoreState) => storeState.marketCampaign.marketCampaignAudience,
  );
  const selectedAudienceCount =
    draftStepData?.draft_step_data?.step_two_data?.selected_rows?.length || 0;

  const { data: templateData } = useSelector(
    (state: IStoreState) => state.marketCampaign.marketCampaignChannel,
  );
  const { data: whatsAppTemplate, loading: whatsAppTemplateLoading } =
    useSelector(
      (state: IStoreState) => state.whatsappTemplate.whatsAppTemplate,
    );

  const [channels, setChannels] = React.useState<IWhatsAppTemplateData[]>([]);
  const [showLoader, setShowLoader] = React.useState(true);
  const [orgImageUrl, setOrgImageUrl] = React.useState<string | null>(null);

  const handleViewDocumnetClick = (rowData: any) => {
    const fileUrl = rowData.source_link[0];
    setOrgImageUrl(fileUrl);
    previewFile(fileUrl);
  };

  const handleClose = () => {
    dispatch(clearWhatsAppTemplateState());
    dispatch(clearDraftStepState());
    onClose();
  };

  const AnimatedBars = () => (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 1.5 }}>
      {[1, 2, 3, 4].map((bar) => (
        <Box
          key={bar}
          sx={{
            width: 8,
            height: bar * 12 + 8,
            backgroundColor: bar % 2 === 0 ? "primary.main" : "secondary.main",
            borderRadius: 1,
            animation: "pulse 1.5s ease-in-out infinite",
            animationDelay: `${bar * 0.2}s`,
            "@keyframes pulse": {
              "0%, 100%": {
                transform: "scaleY(1)",
                opacity: 1,
              },
              "50%": {
                transform: "scaleY(0.7)",
                opacity: 0.7,
              },
            },
          }}
        />
      ))}
    </Box>
  );

  const FileChip: React.FC<{ row: any; index: number }> = ({ row, index }) => {
    const resolveNameFromUuid = (uuid?: string) => {
      if (!uuid) return undefined;
      const item = (marketCampaignAudienceData?.market_campaign_audience_list ||
        []).find((i: any) => i?.uuid === uuid);
      return item?.name || item?.campaign_name;
    };

    const uuidFromRow =
      typeof row === "string"
        ? row
        : row?.market_campaign_audience_list_uuid ||
        row?.market_campaign_audience_list?.[0]?.uuid;

    const displayName =
      row?.audience_name ||
      row?.name ||
      row?.campaign_name ||
      resolveNameFromUuid(uuidFromRow) ||
      row?.market_campaign_audience_list?.[0]?.name ||
      row?.market_campaign_audience_list?.[0]?.campaign_name ||
      "Unnamed";

    return (
      <Box
        key={index}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.300",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* File Icon */}
        <Box
          sx={{
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            backgroundColor:
              row?.audience_source?.toUpperCase() === "CSV"
                ? "rgba(76, 175, 80, 0.1)"
                : "rgba(33, 150, 243, 0.1)",
            flexShrink: 0,
          }}
        >
          {row?.audience_source?.toUpperCase() === "CSV" ? (
            <img
              src={CSVIcon}
              style={{ width: 20, height: 20 }}
              alt="csv-file-icon"
            />
          ) : (
            <img
              src={ExcelIcon}
              style={{ width: 20, height: 20 }}
              alt="excel-file-icon"
            />
          )}
        </Box>

        {/* Audience Name */}
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            lineHeight: 1.3,
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={displayName}
        >
          {displayName}
        </Typography>

        {/* Open Link Button */}
        {row?.source_link?.[0] && (
          <IconButton
            size="small"
            onClick={() => handleViewDocumnetClick(row)}
            title="Open in new tab"
            sx={{
              ml: 0.5,
              p: 0.5,
              color: "grey.500",
              "&:hover": {
                color: "primary.main",
                backgroundColor: "primary.lighter",
              },
            }}
          >
            <OpenInNew sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  // Show loader for 3 seconds when component mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Fetch initial data
  React.useEffect(() => {
    if (!data?.market_campaign_uuid) return;

    dispatch(
      fetchMarketCampaignAudienceAsync(data.market_campaign_uuid, () => { }),
    );
    dispatch(fetchMarketCampaignChannelListAsync(data.market_campaign_uuid));
    dispatch(
      fetchMarketCampaignDraftStepsAsync(data.market_campaign_uuid, () => { }),
    );
  }, [data?.market_campaign_uuid, dispatch]);

  // Fetch WhatsApp template when templateData changes
  React.useEffect(() => {
    if (templateData?.template_name && templateData?.company_outlet_uuid) {
      dispatch(
        fetchWhatsAppTemplateAsync({
          templateName: templateData.template_name,
          companyOutletUuid: templateData.company_outlet_uuid,
        }),
      );

      dispatch(
        // New signature: positional parameters
        fetchWhatsAppTemplateListAsync(
          1, // pageNumber
          10, // rowsInPerPage
          10, // limit
          "", // status
          { fromDate: "", toDate: "" }, // date
          [], // searchValue
          templateData.company_outlet_uuid, // company_outlet_uuid
          templateData.template_name, // searchByName
          undefined, // searchByTemplateId
        ),
      );
    }
  }, [templateData, dispatch]);

  React.useEffect(() => {
    if (metaTemplates && metaTemplates.length > 0) {
      const formattedTemplates = metaTemplates.map((template: any) => {
        const components = template.components || [];
        return {
          ...template,
          header: components.find((c: any) => c.type === "HEADER") || {
            type: "HEADER",
            text: "",
            format: null,
          },
          body: components.find((c: any) => c.type === "BODY") || {
            type: "BODY",
            text: "",
          },
          footer: components.find((c: any) => c.type === "FOOTER") || {
            type: "FOOTER",
            text: "",
          },
          buttons: components.find((c: any) => c.type === "BUTTONS") || {
            type: "BUTTONS",
            buttons: [],
          },
        };
      });
      setChannels(formattedTemplates);
    }
  }, [metaTemplates]);

  return (
    <>
      {/* Loading Modal */}
      <Dialog
        open={showLoader}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 3,
            textAlign: "center",
            minHeight: 200,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AnimatedBars />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Hold on! We're processing your data.
          </Typography>
        </DialogContent>
      </Dialog>

      <RightPanel
        heading={formatText(data?.campaign_name) + " Campaign Details"}
        open={open}
        width="70%"
        onClose={handleClose}
        actionButtons={() => {
          return (
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              spacing={1.5}
            >
              <Stack direction={"row"} spacing={1.5}>
                <Button variant="contained" onClick={handleClose}>
                  <Close sx={{ fontSize: 18, mr: 0.5 }} />
                  Close
                </Button>
              </Stack>
            </Stack>
          );
        }}
      >
        <Grid
          container
          spacing={1}
          sx={{ flexDirection: `${isDesktop ? "row" : "column"}` }}
        >
          <Grid item xs={12} md={7.8}>
            <Card sx={{ mb: 1.5, p: 0 }}>
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Lightbulb
                      sx={{
                        fontSize: 20,
                        color: "primary.main",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Basic Info
                    </Typography>
                  </Box>
                }
                titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Grid
                  container
                  spacing={1.5}
                  sx={{ flexDirection: `${isDesktop ? "row" : "column"}` }}
                >
                  <GridItem label="Sending Type" value={data?.sending_type} />

                  <GridItem
                    label="Description"
                    value={data?.campaign_description}
                  />
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 1.5, p: 0 }}>
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Group
                      sx={{
                        fontSize: 20,
                        color: "primary.main",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Selected Audiences
                    </Typography>
                    <Chip
                      label={
                        selectedAudienceCount > 0 ? selectedAudienceCount : "--"
                      }
                      size="small"
                      color="primary"
                      sx={{
                        fontWeight: 600,
                        minWidth: 32,
                      }}
                    />
                  </Box>
                }
                titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                {/* Selected Audiences Section */}
                {draftStepData?.draft_step_data?.step_two_data?.selected_rows
                  ?.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        p: 0.75,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[200]}`,
                      }}
                    >
                      {draftStepData?.draft_step_data?.step_two_data?.selected_rows?.map(
                        (row: any, index: number) => (
                          <FileChip row={row} index={index} />
                        ),
                      )}
                    </Box>
                  )}

                {/* Empty State */}
                {!draftStepData?.draft_step_data?.step_two_data?.selected_rows
                  ?.length && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        textAlign: "center",
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2,
                        border: `1px dashed ${theme.palette.grey[300]}`,
                      }}
                    >
                      <Group
                        sx={{
                          fontSize: 40,
                          color: theme.palette.grey[400],
                          mb: 0.5,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No audiences selected
                      </Typography>
                    </Box>
                  )}
              </CardContent>
            </Card>

            <Card sx={{ p: 0 }}>
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <AutoAwesomeMosaic
                      sx={{
                        fontSize: 20,
                        color: "primary.main",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {templateData?.template_name
                        ? formatText(
                          templateData?.template_name?.split("_").join(" "),
                        ) + " Template Details"
                        : "Template Details"}
                    </Typography>
                  </Box>
                }
                titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Typography
                  sx={{ ml: 0.5, mb: 0.5 }}
                  color="textSecondary"
                  variant="h6"
                  fontWeight="700"
                >
                  Template Variables
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Column Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Column Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templateData?.module_variables_columns_name?.length ? (
                        templateData.module_variables_columns_name.map(
                          (column, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {column.type.split("_").join(" ") || "-"}
                              </TableCell>
                              <TableCell>
                                {column.value.split("_").join(" ") || "-"}
                              </TableCell>
                            </TableRow>
                          ),
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            align="center"
                            sx={{ color: "text.secondary" }}
                          >
                            No template variables available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Mobile Preview - Below Template Details */}
            {!isDesktop && (
              <Card sx={{ mt: 1.5, p: 0 }}>
                <CardHeader
                  title={
                    <Typography variant="subtitle1" fontWeight={600}>
                      Template Preview
                    </Typography>
                  }
                  titleTypographyProps={{ variant: "h4", fontWeight: 600 }}
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Box
                    sx={{
                      backgroundColor: "#efeae2",
                      backgroundImage: `url(${WhatsappBgImg})`,
                      backgroundRepeat: "repeat",
                      backgroundSize: "auto",
                      backgroundBlendMode: "multiply",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      overflowX: "hidden",
                      pt: 2,
                      pb: 2,
                      pl: 2,
                      pr: 2,
                      minHeight: "auto",
                    }}
                  >
                    {templateData ? (
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                        }}
                      >
                        <PreviewTemplate
                          bgImage={false}
                          template={convertToWhatsAppTemplateData(
                            templateData,
                            channels[0],
                          )}
                          loading={whatsAppTemplateLoading}
                          headerType={
                            templateData?.header_media?.[0]?.type || ""
                          }
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          backdropFilter: "blur(10px)",
                          borderRadius: 3,
                          p: 2.5,
                          border: "2px dashed",
                          borderColor: "primary.main",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                              "linear-gradient(45deg, rgba(25, 118, 210, 0.05) 25%, transparent 25%), linear-gradient(-45deg, rgba(25, 118, 210, 0.05) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(25, 118, 210, 0.05) 75%), linear-gradient(-45deg, transparent 75%, rgba(25, 118, 210, 0.05) 75%)",
                            backgroundSize: "20px 20px",
                            backgroundPosition:
                              "0 0, 0 10px, 10px -10px, -10px 0px",
                            opacity: 0.3,
                          }}
                        />
                        <Stack
                          spacing={2}
                          alignItems="center"
                          sx={{ position: "relative", zIndex: 1 }}
                        >
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              backgroundColor: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 8px 32px rgba(25, 118, 210, 0.3)",
                              position: "relative",
                            }}
                          >
                            <WhatsApp
                              sx={{
                                fontSize: 40,
                                color: "white",
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                top: -5,
                                right: -5,
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                backgroundColor: "secondary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "3px solid white",
                              }}
                            >
                              <Preview sx={{ fontSize: 16, color: "white" }} />
                            </Box>
                          </Box>
                          <Stack spacing={1} alignItems="center">
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              color="primary.main"
                              textAlign="center"
                            >
                              Template Preview
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              textAlign="center"
                              sx={{ maxWidth: 300 }}
                            >
                              Select a template from the campaign details to see
                              a live preview of how your WhatsApp message will
                              appear
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    )}

                    {!whatsAppTemplate && templateData && (
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          borderRadius: 3,
                          p: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: "50%",
                            backgroundColor: "warning.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1.5,
                          }}
                        >
                          <Preview sx={{ fontSize: 30, color: "white" }} />
                        </Box>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          color="warning.main"
                          textAlign="center"
                          gutterBottom
                        >
                          Preview Loading
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                        >
                          Generating template preview...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* PreviewTemplate section - Desktop Only */}
          {isDesktop && (
            <Grid
              item
              xs={12}
              md={4.2}
              sx={{
                backgroundColor: "#efeae2",
                backgroundImage: `url(${WhatsappBgImg})`,
                backgroundRepeat: "repeat",
                backgroundSize: "auto",
                backgroundBlendMode: "multiply",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                overflowX: "hidden",
                pt: 1,
                pb: 1,
                mt: 1.5,
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
              }}
            >
              {templateData ? (
                <Box
                  sx={{
                    height: "70vh",

                    display: "flex",

                    // alignItems: "flex-end",
                    // overflow: "scroll",
                  }}
                >
                  <PreviewTemplate
                    bgImage={false}
                    template={convertToWhatsAppTemplateData(
                      templateData,
                      channels[0],
                    )}
                    loading={whatsAppTemplateLoading}
                    headerType={templateData?.header_media?.[0]?.type || ""}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "70vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 3,
                    p: 2.5,
                    mx: 1.5,
                    border: "2px dashed",
                    borderColor: "primary.main",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Background Pattern */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(45deg, rgba(25, 118, 210, 0.05) 25%, transparent 25%), linear-gradient(-45deg, rgba(25, 118, 210, 0.05) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(25, 118, 210, 0.05) 75%), linear-gradient(-45deg, transparent 75%, rgba(25, 118, 210, 0.05) 75%)",
                      backgroundSize: "20px 20px",
                      backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                      opacity: 0.3,
                    }}
                  />

                  {/* Main Content */}
                  <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ position: "relative", zIndex: 1 }}
                  >
                    {/* Icon Container */}
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 32px rgba(25, 118, 210, 0.3)",
                        position: "relative",
                      }}
                    >
                      <WhatsApp
                        sx={{
                          fontSize: 40,
                          color: "white",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: -5,
                          right: -5,
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          backgroundColor: "secondary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "3px solid white",
                        }}
                      >
                        <Preview sx={{ fontSize: 16, color: "white" }} />
                      </Box>
                    </Box>

                    {/* Text Content */}
                    <Stack spacing={1} alignItems="center">
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="primary.main"
                        textAlign="center"
                      >
                        Template Preview
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        textAlign="center"
                        sx={{ maxWidth: 300 }}
                      >
                        Select a template from the campaign details to see a
                        live preview of how your WhatsApp message will appear
                      </Typography>
                    </Stack>

                    {/* Feature List */}
                    <Stack spacing={1} sx={{ width: "100%", maxWidth: 280 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        }}
                      >
                        <Article sx={{ fontSize: 16, color: "primary.main" }} />
                        <Typography variant="body2" color="text.secondary">
                          View message layout and formatting
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        }}
                      >
                        <Preview sx={{ fontSize: 16, color: "primary.main" }} />
                        <Typography variant="body2" color="text.secondary">
                          Preview with actual variable values
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        }}
                      >
                        <WhatsApp
                          sx={{ fontSize: 16, color: "primary.main" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          See how it appears on WhatsApp
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Action Hint */}
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: "rgba(25, 118, 210, 0.1)",
                        border: "1px solid",
                        borderColor: "primary.light",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="primary.main"
                        fontWeight={600}
                        textAlign="center"
                        display="block"
                      >
                        💡 Tip: Check the "Template Details" section above to
                        see available templates
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {!whatsAppTemplate && templateData && (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: 3,
                    p: 2,
                    mx: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      backgroundColor: "warning.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    <Preview sx={{ fontSize: 30, color: "white" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="warning.main"
                    textAlign="center"
                    gutterBottom
                  >
                    Preview Loading
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Generating template preview...
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </RightPanel>

      {preview.open && (
        <ViewImageDialog
          open={preview.open}
          file={preview.file}
          fileType={preview.type}
          orgImageUrl={orgImageUrl || ""}
          onClose={closePreview}
        />
      )}
    </>
  );
};
